import { useState, useMemo, useEffect } from 'react'
import { StatusBadge, Avatar } from './UI'
import { fmt, CREATOR_COLORS, isPaidStatus, STATUS_CONFIG } from '../lib/constants'
import { fetchAllBrandNotes, addBrandNote, deleteBrandNote } from '../lib/supabase'

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const diff = (Date.now() - new Date(dateStr)) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  const days = Math.floor(diff / 86400)
  if (days < 30) return `${days}d ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

function healthScore(b) {
  // Score: green (good), yellow (needs attention), red (cold)
  const daysSinceLast = b.lastDealDate ? Math.floor((Date.now() - new Date(b.lastDealDate)) / 86400000) : 999
  const dealCount = b.dealCount
  const hasActive = b.isActive
  if (hasActive && dealCount >= 3) return { color: '#10b981', label: 'Hot', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.25)' }
  if (hasActive || daysSinceLast < 60) return { color: '#f59e0b', label: 'Warm', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.25)' }
  return { color: '#ef4444', label: 'Cold', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.25)' }
}

const dtTh = { padding: '7px 12px', fontSize: 10, fontWeight: 600, color: 'var(--text3)', textAlign: 'left', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.3px' }

export default function CRM({ deals, isMobile, privacyMode, currentUser, onCopyToPersonal, leads, isAdmin }) {
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState('totalRevenue')
  const [sortDir, setSortDir] = useState('desc')
  const [expandedBrand, setExpandedBrand] = useState(null)
  const [groupBy, setGroupBy] = useState('brand')   // 'brand' | 'client'
  const [filterStatus, setFilterStatus] = useState('All')
  const [filterHealth, setFilterHealth] = useState('All')
  const [brandNotes, setBrandNotes] = useState({})
  const [noteInput, setNoteInput] = useState('')
  const [noteLoading, setNoteLoading] = useState(false)
  const hide = privacyMode

  // Load all brand notes on mount
  useEffect(() => {
    fetchAllBrandNotes().then(({ data }) => {
      if (!data) return
      const map = {}
      data.forEach(n => {
        if (!map[n.brand]) map[n.brand] = []
        map[n.brand].push(n)
      })
      setBrandNotes(map)
    })
  }, [])

  const handleAddNote = async (brand) => {
    if (!noteInput.trim()) return
    setNoteLoading(true)
    const { data } = await addBrandNote(brand, noteInput.trim(), currentUser?.full_name || currentUser?.agent_name || 'Admin')
    if (data?.[0]) {
      setBrandNotes(prev => ({ ...prev, [brand]: [data[0], ...(prev[brand] || [])] }))
    }
    setNoteInput('')
    setNoteLoading(false)
  }

  const handleDeleteNote = async (brand, noteId) => {
    await deleteBrandNote(noteId)
    setBrandNotes(prev => ({ ...prev, [brand]: (prev[brand] || []).filter(n => n.id !== noteId) }))
  }

  // Notes are stored by name, and some names are both a brand and a client
  // (Rivian, for one). Prefixing the client side keeps the two sets apart.
  const noteKey = (name) => (groupBy === 'client' ? `client:${name}` : name)

  // Rows group by brand or by client. `name` is whichever one you're grouping
  // on; `related` is the other side — the clients who bought a brand, or the
  // brands a client has bought. Grouping by client is how repeat buyers like
  // Hashtag Paid read as one account instead of nineteen unrelated rows.
  const other = groupBy === 'brand' ? 'client' : 'brand'
  const brands = useMemo(() => {
    const map = {}
    deals.forEach(d => {
      const key = d[groupBy]
      if (!key) return
      if (!map[key]) map[key] = {
        brand: key,          // the group's name, whichever dimension it is
        related: new Set(),  // the opposite dimension
        deals: [],
        creators: new Set(),
        agents: new Set(),
        contacts: new Map(),
        totalRevenue: 0,
        paidRevenue: 0,
        statuses: {},
        firstDealDate: d.date || d.created_at || '',
        lastDealDate: d.date || d.created_at || '',
      }
      const b = map[key]
      b.deals.push(d)
      b.totalRevenue += d.deal_amount || 0
      if (isPaidStatus(d.status)) b.paidRevenue += d.deal_amount || 0
      if (d.creator) b.creators.add(d.creator)
      if (d.agent) b.agents.add(d.agent)
      if (d.status) b.statuses[d.status] = (b.statuses[d.status] || 0) + 1
      if (d[other] && d[other] !== key) b.related.add(d[other])
      if (d.contact_person && !b.contacts.has(d.contact_person)) {
        b.contacts.set(d.contact_person, { email: d.email || '' })
      }
      const dt = d.date || d.created_at || ''
      if (dt && (!b.firstDealDate || dt < b.firstDealDate)) b.firstDealDate = dt
      if (dt && (!b.lastDealDate || dt > b.lastDealDate)) b.lastDealDate = dt
    })
    return Object.values(map).map(b => ({
      ...b,
      creators: [...b.creators],
      agents: [...b.agents],
      related: [...b.related],
      client: [...b.related].join(', '),   // kept for search and CSV export
      contacts: [...b.contacts.entries()].map(([name, info]) => ({ name, email: info.email })),
      dealCount: b.deals.length,
      isActive: b.deals.some(d => !isPaidStatus(d.status) && d.status !== 'Done' && d.status !== 'Paid/Done'),
      isRepeat: b.deals.length >= 3,
    }))
  }, [deals, groupBy, other])

  // Filter
  const filtered = useMemo(() => {
    let b = brands
    if (search) {
      const q = search.toLowerCase()
      b = b.filter(x => [x.brand, x.client, ...x.creators, ...x.contacts.map(c => c.name)].some(f => f && f.toLowerCase().includes(q)))
    }
    if (filterStatus === 'Active') b = b.filter(x => x.isActive)
    if (filterStatus === 'Completed') b = b.filter(x => !x.isActive)
    if (filterHealth === 'Hot') b = b.filter(x => healthScore(x).label === 'Hot')
    if (filterHealth === 'Warm') b = b.filter(x => healthScore(x).label === 'Warm')
    if (filterHealth === 'Cold') b = b.filter(x => healthScore(x).label === 'Cold')
    return b
  }, [brands, search, filterStatus, filterHealth])

  // Sort
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av = a[sortField], bv = b[sortField]
      if (typeof av === 'string') av = av.toLowerCase()
      if (typeof bv === 'string') bv = bv.toLowerCase()
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [filtered, sortField, sortDir])

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortField(field); setSortDir('desc') }
  }
  const sort = (f) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''

  const totalBrandRevenue = filtered.reduce((s, b) => s + b.totalRevenue, 0)
  const totalBrandDeals = filtered.reduce((s, b) => s + b.dealCount, 0)
  const activeBrands = filtered.filter(b => b.isActive).length
  const repeatBrands = filtered.filter(b => b.isRepeat).length

  // Admin CRM export
  const handleExport = () => {
    if (!sorted.length) return
    const rows = [[
      groupBy === 'brand' ? 'Brand' : 'Client',
      groupBy === 'brand' ? 'Clients' : 'Brands',
      'Deals', 'Total Revenue', 'Paid Revenue', 'Creators', 'Agents',
      'Contacts', 'Contact Emails', 'Health', 'Status', 'First Deal', 'Last Deal', 'Notes',
    ]]
    sorted.forEach(b => {
      const h = healthScore(b)
      const notes = (brandNotes[noteKey(b.brand)] || []).map(n => n.note).join(' | ')
      rows.push([
        b.brand, b.client, b.dealCount,
        b.totalRevenue, b.paidRevenue,
        b.creators.join(', '), b.agents.join(', '),
        b.contacts.map(c => c.name).join(', '),
        b.contacts.map(c => c.email).filter(Boolean).join(', '),
        h.label, b.isActive ? 'Active' : 'Completed',
        b.firstDealDate, b.lastDealDate, notes
      ])
    })
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `AgencyBase-CRM-Export-${new Date().toISOString().slice(0,10)}.csv`
    a.click(); URL.revokeObjectURL(url)
  }

  const th = (field) => ({
    padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700,
    color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px',
    borderBottom: '1px solid var(--border)', cursor: field ? 'pointer' : 'default',
    userSelect: 'none', whiteSpace: 'nowrap',
    background: sortField === field ? 'rgba(249,115,22,0.04)' : 'transparent',
  })
  const td = { padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 13, verticalAlign: 'middle' }

  return (
    <div className="fade-in">
      {/* Hero header */}
      <div style={{
        background: 'var(--gradient-card)', border: '1px solid var(--card-border)',
        borderRadius: 'var(--radius-lg)', padding: isMobile ? 20 : 32,
        marginBottom: 24, position: 'relative', overflow: 'hidden',
        boxShadow: 'var(--shadow-sm)'
      }}>
        <div style={{ position: 'absolute', inset: 0, boxShadow: 'var(--shadow-inner)', borderRadius: 'var(--radius-lg)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(249,115,22,0.04)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -40, right: 60, width: 120, height: 120, borderRadius: '50%', background: 'rgba(59,130,246,0.03)', pointerEvents: 'none' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 12,
                background: 'linear-gradient(135deg, #f97316, #ef4444)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 700, color: '#fff', fontFamily: 'var(--font-display)',
                boxShadow: '0 4px 12px rgba(249,115,22,0.3)'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
              <div>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 24 : 30, fontWeight: 700, letterSpacing: '-0.6px', lineHeight: 1.1 }}>Brand CRM</h1>
                <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>Manage relationships, track deals, and grow partnerships</p>
              </div>
            </div>
            {isAdmin && (
              <button onClick={handleExport} className="btn-hover" style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: 'var(--bg3)', border: '1px solid var(--border)',
                color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                whiteSpace: 'nowrap'
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Export CSV
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center', padding: '8px 16px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', minWidth: 70 }}>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--accent)' }}>{filtered.length}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 500 }}>Brands</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px 16px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', minWidth: 70 }}>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-display)', color: '#3b82f6' }}>{totalBrandDeals}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 500 }}>Deals</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px 16px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', minWidth: 70 }}>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-display)', color: '#10b981' }}>{hide ? '•••' : fmt(totalBrandRevenue)}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 500 }}>Revenue</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px 16px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', minWidth: 70 }}>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-display)', color: '#f59e0b' }}>{repeatBrands}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 500 }}>Repeat</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters bar */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center'
      }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search brands, clients, creators, contacts..."
            style={{
              width: '100%', padding: '9px 14px 9px 34px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--bg3)',
              color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none'
            }}
          />
        </div>
        {/* Group rows by brand or by client */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)', padding: 3 }}>
          {[['brand', 'Brands'], ['client', 'Clients']].map(([key, label]) => (
            <button key={key} onClick={() => { setGroupBy(key); setExpandedBrand(null) }} style={{
              padding: '5px 12px', borderRadius: 6, border: 'none',
              background: groupBy === key ? 'var(--accent)' : 'transparent',
              color: groupBy === key ? '#fff' : 'var(--text3)',
              fontWeight: 600, fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font-body)',
              transition: 'all 0.15s'
            }}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)', padding: 3 }}>
          {['All', 'Active', 'Completed'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} style={{
              padding: '5px 12px', borderRadius: 6, border: 'none',
              background: filterStatus === s ? 'var(--accent)' : 'transparent',
              color: filterStatus === s ? '#fff' : 'var(--text3)',
              fontWeight: 600, fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font-body)',
              transition: 'all 0.15s'
            }}>{s}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)', padding: 3 }}>
          {['All', 'Hot', 'Warm', 'Cold'].map(h => {
            const colors = { Hot: '#10b981', Warm: '#f59e0b', Cold: '#ef4444', All: 'var(--text3)' }
            return (
              <button key={h} onClick={() => setFilterHealth(h)} style={{
                padding: '5px 10px', borderRadius: 6, border: 'none',
                background: filterHealth === h ? (h === 'All' ? 'var(--accent)' : colors[h] + '20') : 'transparent',
                color: filterHealth === h ? (h === 'All' ? '#fff' : colors[h]) : 'var(--text3)',
                fontWeight: 600, fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font-body)',
                transition: 'all 0.15s'
              }}>{h === 'All' ? 'All' : `${h}`}</button>
            )
          })}
        </div>
      </div>

      {/* Table */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th('client')} onClick={() => toggleSort('client')}>{groupBy === 'brand' ? 'Client' : 'Brands'}{sort('client')}</th>
                <th style={th('brand')} onClick={() => toggleSort('brand')}>{groupBy === 'brand' ? 'Brand' : 'Client'}{sort('brand')}</th>
                {!isMobile && <th style={th()}>Health</th>}
                <th style={th('dealCount')} onClick={() => toggleSort('dealCount')}>Deals{sort('dealCount')}</th>
                <th style={th('totalRevenue')} onClick={() => toggleSort('totalRevenue')}>Revenue{sort('totalRevenue')}</th>
                {!isMobile && <th style={th()}>Creators</th>}
                {!isMobile && <th style={th()}>Contact</th>}
                <th style={th('lastDealDate')} onClick={() => toggleSort('lastDealDate')}>Last Activity{sort('lastDealDate')}</th>
              </tr>
            </thead>
            {sorted.map((b) => {
              const isExpanded = expandedBrand === b.brand
              const hs = healthScore(b)
              const notes = brandNotes[noteKey(b.brand)] || []
              const brandHue = b.brand.charCodeAt(0) * 7 % 360
              return (
                <tbody key={b.brand}>
                  <tr
                    className="row-hover"
                    onClick={() => { setExpandedBrand(isExpanded ? null : b.brand); setNoteInput('') }}
                    style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                  >
                    <td style={{ ...td, color: 'var(--text2)', fontSize: 12, fontWeight: 500, maxWidth: 190 }}>
                      {hide ? '•••••' : !b.related.length ? '--' : (
                        <span title={b.related.join(', ')} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {b.related[0]}
                          {b.related.length > 1 && (
                            <span style={{ color: 'var(--text3)' }}> +{b.related.length - 1}</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                          background: `hsl(${brandHue}, 35%, 22%)`,
                          border: `1.5px solid hsl(${brandHue}, 45%, 40%)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, color: `hsl(${brandHue}, 45%, 65%)`,
                          fontFamily: 'var(--font-display)'
                        }}>
                          {b.brand.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 600, fontSize: 13 }}>{b.brand}</span>
                            {b.isRepeat && (
                              <span title="Repeat client (3+ deals)" style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700,
                                background: 'rgba(245,158,11,0.15)', color: '#f59e0b',
                                border: '1px solid rgba(245,158,11,0.25)', letterSpacing: '0.3px'
                              }}>★ REPEAT</span>
                            )}
                            {notes.length > 0 && (
                              <span title={`${notes.length} note${notes.length > 1 ? 's' : ''}`} style={{ fontSize: 11, color: 'var(--text3)' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    {!isMobile && (
                      <td style={td}>
                        <span style={{
                          padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                          background: hs.bg, color: hs.color, border: `1px solid ${hs.border}`,
                          letterSpacing: '0.3px'
                        }}>{hs.label}</span>
                      </td>
                    )}
                    <td style={{ ...td, fontWeight: 700, fontFamily: 'var(--font-display)', textAlign: 'center' }}>{b.dealCount}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--accent)' }}>
                        {hide ? '•••••' : fmt(b.totalRevenue)}
                      </div>
                      {!hide && b.paidRevenue > 0 && (
                        <div style={{ fontSize: 10, color: '#10b981', fontWeight: 600 }}>{fmt(b.paidRevenue)} paid</div>
                      )}
                    </td>
                    {!isMobile && (
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          {b.creators.slice(0, 3).map(c => (
                            <span key={c} style={{
                              padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                              background: (CREATOR_COLORS[c] || '#6b7280') + '1a',
                              color: CREATOR_COLORS[c] || '#6b7280',
                              border: `1px solid ${(CREATOR_COLORS[c] || '#6b7280')}25`
                            }}>{c}</span>
                          ))}
                          {b.creators.length > 3 && <span style={{ fontSize: 10, color: 'var(--text3)', padding: '2px 3px' }}>+{b.creators.length - 3}</span>}
                        </div>
                      </td>
                    )}
                    {!isMobile && (
                      <td style={td}>
                        {hide ? '•••••' : (b.contacts.length > 0 ? (
                          <div>
                            <div style={{ fontWeight: 500, fontSize: 12 }}>{b.contacts[0].name}</div>
                            {b.contacts[0].email && (
                              <a href={`mailto:${b.contacts[0].email}`} style={{ fontSize: 10, color: '#3b82f6' }} onClick={e => e.stopPropagation()}>{b.contacts[0].email}</a>
                            )}
                            {b.contacts.length > 1 && <div style={{ fontSize: 10, color: 'var(--text3)' }}>+{b.contacts.length - 1} more</div>}
                          </div>
                        ) : <span style={{ color: 'var(--text3)', fontSize: 12 }}>--</span>)}
                      </td>
                    )}
                    <td style={{ ...td, fontSize: 12, color: 'var(--text2)' }}>
                      <div style={{ fontWeight: 500 }}>{timeAgo(b.lastDealDate)}</div>
                      {b.lastDealDate && <div style={{ fontSize: 10, color: 'var(--text3)' }}>{new Date(b.lastDealDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>}
                    </td>
                  </tr>

                  {/* Expanded panel */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={isMobile ? 4 : 7} style={{ padding: 0, borderBottom: '2px solid var(--accent)' }}>
                        <div style={{ background: 'var(--bg)', padding: isMobile ? 14 : 24, borderTop: '1px solid var(--border)' }}>
                          {/* Top row: Overview, Pipeline, Contacts, Creators */}
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
                            {/* Revenue overview */}
                            <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: 16, border: '1px solid var(--border)' }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Revenue</div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <div>
                                  <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--accent)' }}>{hide ? '•••••' : fmt(b.totalRevenue)}</div>
                                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>total pipeline</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)', color: '#10b981' }}>{hide ? '•••••' : fmt(b.paidRevenue)}</div>
                                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>collected</div>
                                </div>
                              </div>
                              {b.totalRevenue > 0 && !hide && (
                                <div style={{ marginTop: 10 }}>
                                  <div style={{ height: 5, borderRadius: 3, background: 'var(--bg3)', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', borderRadius: 3, background: 'linear-gradient(90deg, #10b981, #34d399)', width: `${Math.min((b.paidRevenue / b.totalRevenue) * 100, 100)}%`, transition: 'width 0.5s' }} />
                                  </div>
                                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>{Math.round((b.paidRevenue / b.totalRevenue) * 100)}% collected</div>
                                </div>
                              )}
                            </div>

                            {/* Pipeline by status */}
                            <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: 16, border: '1px solid var(--border)' }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Pipeline</div>
                              {Object.entries(b.statuses).sort((a, b) => b[1] - a[1]).map(([status, count]) => {
                                const sc = STATUS_CONFIG[status]
                                return (
                                  <div key={status} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: sc?.dot || '#6b7280' }} />
                                      <span style={{ fontSize: 12, color: 'var(--text2)' }}>{status}</span>
                                    </div>
                                    <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-display)' }}>{count}</span>
                                  </div>
                                )
                              })}
                            </div>

                            {/* Contacts + Creators */}
                            <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: 16, border: '1px solid var(--border)' }}>
                              {b.contacts.length > 0 && (
                                <div style={{ marginBottom: 14 }}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Contacts</div>
                                  {b.contacts.map((c, ci) => (
                                    <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                      <div style={{
                                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                                        background: 'var(--bg3)', border: '1px solid var(--border)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 9, fontWeight: 700, color: 'var(--text2)'
                                      }}>{c.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}</div>
                                      <div>
                                        <div style={{ fontSize: 12, fontWeight: 600 }}>{hide ? '•••••' : c.name}</div>
                                        {c.email && !hide && <a href={`mailto:${c.email}`} style={{ fontSize: 10, color: '#3b82f6', textDecoration: 'none' }}>{c.email}</a>}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Creators</div>
                              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                {b.creators.map(c => (
                                  <span key={c} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                    background: (CREATOR_COLORS[c] || '#6b7280') + '15',
                                    color: CREATOR_COLORS[c] || '#6b7280'
                                  }}>{c}</span>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Notes section */}
                          <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: 16, border: '1px solid var(--border)', marginBottom: 20 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                              Notes ({notes.length})
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginBottom: notes.length > 0 ? 12 : 0 }}>
                              <input
                                value={noteInput}
                                onChange={e => setNoteInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleAddNote(noteKey(b.brand))}
                                onClick={e => e.stopPropagation()}
                                placeholder={`Add a note about this ${groupBy}...`}
                                style={{
                                  flex: 1, padding: '8px 12px', borderRadius: 7,
                                  border: '1px solid var(--border)', background: 'var(--bg)',
                                  color: 'var(--text)', fontSize: 12, fontFamily: 'var(--font-body)', outline: 'none'
                                }}
                              />
                              <button
                                onClick={e => { e.stopPropagation(); handleAddNote(noteKey(b.brand)) }}
                                disabled={noteLoading || !noteInput.trim()}
                                style={{
                                  padding: '8px 16px', borderRadius: 7, border: 'none',
                                  background: noteInput.trim() ? 'var(--accent)' : 'var(--bg3)',
                                  color: noteInput.trim() ? '#fff' : 'var(--text3)',
                                  fontSize: 12, fontWeight: 600, cursor: noteInput.trim() ? 'pointer' : 'default',
                                  fontFamily: 'var(--font-body)', transition: 'all 0.15s'
                                }}
                              >{noteLoading ? '...' : 'Add'}</button>
                            </div>
                            {notes.map(n => (
                              <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0', borderTop: '1px solid var(--border)' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5 }}>{n.note}</div>
                                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{n.created_by} · {timeAgo(n.created_at)}</div>
                                </div>
                                <button
                                  onClick={e => { e.stopPropagation(); handleDeleteNote(noteKey(b.brand), n.id) }}
                                  style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 14, padding: '0 4px', opacity: 0.5, flexShrink: 0 }}
                                  title="Delete note"
                                >×</button>
                              </div>
                            ))}
                          </div>

                          {/* Copy to personal CRM */}
                          {onCopyToPersonal && (
                            <div style={{ marginBottom: 20 }}>
                              {(leads || []).some(l => l.source === 'agency_crm' && l.source_brand === b.brand && l.owner_id === currentUser?.id)
                                ? <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', color: '#10b981', fontSize: 12, fontWeight: 600 }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                    Already in your CRM
                                  </div>
                                : <button
                                    onClick={e => { e.stopPropagation(); onCopyToPersonal(b) }}
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 6,
                                      padding: '8px 16px', borderRadius: 8, border: '1px solid var(--accent)',
                                      background: 'rgba(249,115,22,0.08)', color: 'var(--accent)',
                                      fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                                      fontFamily: 'var(--font-body)'
                                    }}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
                                    Copy to My CRM
                                  </button>
                              }
                            </div>
                          )}

                          {/* Deal history */}
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                            Deal History ({b.dealCount})
                          </div>
                          <div style={{ background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  <th style={dtTh}>Creator</th>
                                  <th style={dtTh}>Deliverables</th>
                                  <th style={dtTh}>Amount</th>
                                  <th style={dtTh}>Status</th>
                                  <th style={dtTh}>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[...b.deals].sort((a, c) => (c.date || '').localeCompare(a.date || '')).map((d, di) => (
                                  <tr key={d.id || di}>
                                    <td style={{ padding: '8px 12px', fontSize: 12, borderBottom: '1px solid var(--border)' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <Avatar name={d.creator} size={20} />
                                        <span style={{ fontWeight: 600, color: CREATOR_COLORS[d.creator] || 'var(--text)' }}>{d.creator}</span>
                                      </div>
                                    </td>
                                    <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text2)', borderBottom: '1px solid var(--border)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.deliverables || '--'}</td>
                                    <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-display)', borderBottom: '1px solid var(--border)' }}>{hide ? '•••••' : (d.deal_amount ? fmt(d.deal_amount) : '--')}</td>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}><StatusBadge status={d.status} /></td>
                                    <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text3)', borderBottom: '1px solid var(--border)' }}>{d.month || '--'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              )
            })}
          </table>
        </div>
        {sorted.length === 0 && (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text3)' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>No brands found</div>
            <div style={{ fontSize: 13 }}>Try adjusting your search or filters</div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ marginTop: 12, display: 'flex', gap: 20, fontSize: 12, color: 'var(--text3)', flexWrap: 'wrap' }}>
        <span>{filtered.length} brands</span>
        <span>Total: <strong style={{ color: 'var(--text)' }}>{hide ? '•••••' : fmt(totalBrandRevenue)}</strong></span>
        <span>Collected: <strong style={{ color: '#10b981' }}>{hide ? '•••••' : fmt(filtered.reduce((s, x) => s + x.paidRevenue, 0))}</strong></span>
        <span>Avg / brand: <strong style={{ color: 'var(--text)' }}>{hide ? '•••••' : fmt(filtered.length > 0 ? totalBrandRevenue / filtered.length : 0)}</strong></span>
      </div>
    </div>
  )
}
